// Contentful adapter — reads from Contentful when content exists, otherwise
// falls back to the existing /api/site-content (which the in-app editor manages).
// This means the site keeps working before AND after a Contentful import.

import * as contentful from "contentful";

const SPACE = process.env.REACT_APP_CONTENTFUL_SPACE_ID;
const TOKEN = process.env.REACT_APP_CONTENTFUL_CDA_TOKEN;
const ENV = process.env.REACT_APP_CONTENTFUL_ENVIRONMENT || "master";

let client = null;
if (SPACE && TOKEN) {
  try {
    client = contentful.createClient({ space: SPACE, accessToken: TOKEN, environment: ENV });
  } catch (e) {
    console.warn("Contentful client init failed:", e);
  }
}

// Normalises an entry by flattening fields and resolving link URLs (we use plain string URLs)
const flat = (entry) => ({ id: entry.sys.id, ...entry.fields });

export async function loadFromContentful() {
  if (!client) return null;
  try {
    const [siteRes, packagesRes, themesRes, galleryRes, testRes, faqRes] = await Promise.all([
      client.getEntries({ content_type: "siteSettings", limit: 1 }),
      client.getEntries({ content_type: "package", order: "fields.order" }),
      client.getEntries({ content_type: "theme", order: "fields.order" }),
      client.getEntries({ content_type: "galleryImage", order: "fields.order" }),
      client.getEntries({ content_type: "testimonial", order: "fields.order" }),
      client.getEntries({ content_type: "faq", order: "fields.order" }),
    ]);

    // If there are no site settings yet, treat Contentful as "not ready" and let backend fallback kick in
    if (!siteRes.items.length) return null;

    const settings = flat(siteRes.items[0]);
    return {
      _source: "contentful",
      site: {
        heroBadge: settings.heroBadge,
        heroScriptWord: settings.heroScriptWord,
        heroTitle: settings.heroTitle,
        heroSubtitle: settings.heroSubtitle,
        heroBody: settings.heroBody,
        heroImageUrl: settings.heroImageUrl,
        aboutTitle: settings.aboutTitle,
        aboutScriptWord: settings.aboutScriptWord,
        aboutBody: settings.aboutBody,
        aboutImageUrl: settings.aboutImageUrl,
        finalCtaTitle: settings.finalCtaTitle,
        finalCtaSubtitle: settings.finalCtaSubtitle,
        contactPhone: settings.contactPhone,
        contactEmail: settings.contactEmail,
        instagramHandle: settings.instagramHandle,
        facebookHandle: settings.facebookHandle,
        serviceArea: settings.serviceArea,
      },
      packages: packagesRes.items.map(flat).map((p) => ({
        id: p.slug, name: p.name, teepees: p.teepees, price: p.price,
        description: p.description, image: p.imageUrl, featured: !!p.featured,
      })),
      themes: themesRes.items.map(flat).map((t) => ({
        id: t.slug, name: t.name, description: t.description, image: t.imageUrl,
      })),
      gallery: galleryRes.items.map(flat).map((g) => g.imageUrl),
      testimonials: testRes.items.map(flat).map((t) => ({
        name: t.name, role: t.role, rating: t.rating, text: t.text, photo: t.photoUrl,
      })),
      faqs: faqRes.items.map(flat).map((f) => ({ q: f.question, a: f.answer })),
    };
  } catch (err) {
    console.warn("Contentful fetch failed, will fall back to backend:", err.message || err);
    return null;
  }
}

export const isContentfulConfigured = !!client;
